import React from 'react';
import { useActor } from '@xstate/react';
import { FormApi, FormStateBasic } from 'informed';
import { ProgressBar } from 'react-loader-spinner';
import MediaQuery from 'react-responsive';
import Yup from 'yup';

import { AuthCoreMachineContext } from '@nw/auth-core';
import { DialCodes } from '@nw/shared/data-access';
import {
  BackArrowIcon,
  ChevronLeftArrowIcon,
  FireIcon,
  SuccessTickIcon
} from '@nw/shared/ui';
import {
  removeEmptyParams,
  verifyURLParamsBwLinkAndRedirectionLink
} from '@nw/shared/utils';

import {
  PublicFormMachineSendType,
  PublicFormMachineStateType
} from '../../../form/machines/PublicFormsMachine/types';
//TODO: Use product constants from shared
import {
  productFormsMachineEvents,
  productFormsMachineTags
} from '../../../product/constants/formMachineConstants';
import {
  AssignConfigEventToContext,
  CallEventsFromConfig,
  PublicProductFormMachineSendType,
  PublicProductFormMachineStateType,
  SetGoToNextSteps
} from '../../../product/machines/PublicFormsMachine/types';

import { progressBarType, stepTypes } from '../../constants/FormConstant';
import StepConfig from '../../stores/models/StepConfig';
import { CustomClassNamesInterface } from '../../types/MiscellaneousTypes';

import {
  ButtonComponent,
  buttonSize,
  ButtonSize,
  buttonVariant,
  ButtonVariant
} from '../ButtonComponent';
import { EmbeddedFormSuccessPage } from '../EmbeddedFormSuccessPage';
import Multistep, { useMultistepApi } from '../ExtendedMultiStepField';
import { FormRenderer } from '../FormRenderer';
import { GoogleCalendarInviteComponent } from '../GoogleCalendarInviteComponent';
import MultiStepVerifyOTP from '../MultiStepVerifyOtpButton';
import {
  NumberedProgressbar,
  ProgressBarWithSteps
} from '../ProgressBarWithSteps';
import { SuccessMessageContainerWithEmailMessage } from '../SuccessMessageContainerWithEmailMessage';

import {
  AdditionalCTAButton,
  CTAAndSubmitButtonContainer,
  CustomUIImage,
  ErrorMessage,
  ExtraFooterUIContainer,
  FormDescription,
  FormFieldSet,
  FormTitle,
  GoBackContainer,
  GoBackText,
  IconContainer,
  LimitedSeatMessageContainer,
  LimitedSeatMessageText,
  LimitedTimeOfferContainer,
  LineBreak,
  MultiStepFormFooterWrapper,
  OTPDescriptionText,
  PhoneNumberText,
  PriceContainer,
  ProgressBarContainer,
  SubmitButtonFrame,
  SubmitButtonText,
  SubmitButtonTextWrapper,
  SuccessDescription,
  SuccessHeading,
  SuccessMessageContainer,
  TnCText
} from './styledComponents';

interface MultiStepRegistrationFormProps {
  eachFormStep: StepConfig;
  formValues: FormStateBasic['values'];
  touched: FormStateBasic['touched'];
  errors: FormStateBasic['errors'];
  formApi: FormApi;
  publicFormMachineState:
  | PublicProductFormMachineStateType
  | PublicFormMachineStateType;
  publicFormMachineSend:
  | PublicProductFormMachineSendType
  | PublicFormMachineSendType;
  validationSchemaObject: Yup.AnyObjectSchema;
  customClassNames: CustomClassNamesInterface;
  renderCustomSuccessPage?: (
    formValues: FormStateBasic['values'],
    publicFormMachineState: PublicFormMachineStateType,
    publicFormMachineSend: PublicFormMachineSendType
  ) => React.ReactElement;
  customHeader?: (eachFormStep: StepConfig) => React.ReactElement;
  getNumberedProgressBarTitles: () => string[];
  t: any;
  handleFormReset?: () => void;
  designSystemType?: string;
  onValidatedBeforeStepActions?: (params: {
    formValues: Record<string, unknown>;
    stepId: string;
    proceed: () => void;
    advanceToNextStep: () => void;
  }) => void;
}

export const MultiStepRegistrationForm: React.FC<
  MultiStepRegistrationFormProps
> = ({
  eachFormStep,
  formApi,
  formValues,
  errors,
  touched,
  publicFormMachineSend,
  publicFormMachineState,
  validationSchemaObject,
  customClassNames,
  renderCustomSuccessPage,
  customHeader,
  getNumberedProgressBarTitles,
  t,
  handleFormReset = () => { },
  designSystemType,
  onValidatedBeforeStepActions
}) => {
    const {
      stepId,
      nextStepDetails,
      previousStepDetails,
      stepFields,
      stepType: currentStepType,
      stepInfo
    } = eachFormStep;

    const { next, back } = useMultistepApi();
    publicFormMachineState.context.back = back;
    publicFormMachineState.context.next = next;

    //NOTE: isLoggedIn & formSubmissionDetails is used to check the eval condition for nextStepId which is defined in config
    const { isLoggedIn, formSubmissionDetails } = publicFormMachineState.context;

    const conditionBasedRedirectionUrls =
      publicFormMachineState.context.productInfo?.registrationMessagesConfig
        ?.success_payment.condition_based_redirection_urls;

    const {
      step_id: nextStepId = '',
      btn_id: nextBtnId,
      btn_text: nextBtnText,
      btn_actions: nextBtnActions
    } = nextStepDetails ?? {};

    const {
      step_id: prevStepId,
      btn_id: prevBtnId,
      btn_text: prevBtnText,
      btn_actions: prevBtnActions
    } = previousStepDetails ?? {};

    //Note: isAuthenticationStep is used to check the OTP filling and verification step
    const isAuthenticationStep = (): boolean => {
      return currentStepType === stepTypes.AUTHENTICATION_STEP;
    };

    const proceedToNextStep = (): void => {
      publicFormMachineSend({
        type: productFormsMachineEvents.assignConfigEventsToContext,
        configEvent: nextBtnActions ?? [],
        values: formValues
      } as AssignConfigEventToContext);
      publicFormMachineSend({
        type: productFormsMachineEvents.callEventsFromConfig
      } as CallEventsFromConfig);
    };

    const handleValidFormSubmission = (): void => {
      if (onValidatedBeforeStepActions) {
        onValidatedBeforeStepActions({
          formValues: formValues as Record<string, unknown>,
          stepId,
          proceed: proceedToNextStep,
          advanceToNextStep: next
        });
        return;
      }

      proceedToNextStep();
    };

    const isCurrentStepValid = (): boolean => {
      const screenValidFn = (
        formApi as FormApi & { screenValid?: () => boolean }
      ).screenValid;

      if (screenValidFn) {
        return screenValidFn();
      }

      return stepFields.every((field) => !formApi.getError(field.field));
    };

    const runValidationAndProceed = (): void => {
      formApi.validate();

      const proceedIfValid = (): void => {
        if (isCurrentStepValid()) {
          handleValidFormSubmission();
        }
      };

      const formState = formApi.getState() as { validating?: number };

      if (formState.validating) {
        const onValidationChange = (): void => {
          const nextFormState = formApi.getState() as { validating?: number };

          if (!nextFormState.validating) {
            (
              formApi as FormApi & {
                emitter?: { removeListener: (event: string, cb: () => void) => void };
              }
            ).emitter?.removeListener('change', onValidationChange);
            proceedIfValid();
          }
        };

        (
          formApi as FormApi & {
            emitter?: { on: (event: string, cb: () => void) => void };
          }
        ).emitter?.on('change', onValidationChange);
        return;
      }

      Promise.resolve().then(proceedIfValid);
    };

    const onClickNextBtn = (): void => {
      const goToNextSteps = publicFormMachineState.context.goToNextSteps;
      if (goToNextSteps.length > 0) {
        publicFormMachineSend({
          type: productFormsMachineEvents.setGoToNextSteps,
          shouldRemove: true
        } as SetGoToNextSteps);
        next();
      } else {
        const shouldUseShadcnDesignSystem =
          designSystemType === 'SHADCN_DESIGN_SYSTEM';

        // SHADCN: use submitForm() so onSubmit/onSubmitFailure callbacks can run.
        // Others: validate current-step fields via informed, then proceed async.
        if (shouldUseShadcnDesignSystem) {
          formApi.submitForm();
        } else {
          runValidationAndProceed();
        }
      }
    };

    const getOTPFieldRef = () => {
      const { authMachineRef } = publicFormMachineState.context;
      if (authMachineRef) {
        const [authCoreMachineState] = useActor(authMachineRef);
        const { context }: { context: AuthCoreMachineContext } =
          authCoreMachineState;
        if (context.otpFieldRef) {
          return context.otpFieldRef;
        } else return null;
      }
      return null;
    };

    const renderVerifyOtp = (): React.ReactElement | null => {
      const otpFieldRef = getOTPFieldRef();

      if (!otpFieldRef) {
        return null;
      }

      return (
        <MultiStepVerifyOTP
          otp={formValues['otp'] as string}
          otpFieldRef={otpFieldRef}
          nextStepButtonId={nextBtnId}
          nextStepButtonText={nextBtnText}
          publicFormMachineSend={publicFormMachineSend}
          nextBtnActions={nextBtnActions}
          formValues={formValues}
          publicFormMachineState={publicFormMachineState}
          customClassNames={customClassNames}
          designSystemType={designSystemType}
        />
      );
    };

    const isSubmitButtonLoading = () => {
      if (
        publicFormMachineState.context.shouldShowGetOTP &&
        publicFormMachineState.hasTag(productFormsMachineTags.otpVerifyBtnLoading)
      ) {
        return false;
      } else return publicFormMachineState.context.isNextAndSubmitBtnLoading;
    };

    const isSubmitButtonDisabled = (): boolean => {
      if (
        publicFormMachineState.context.shouldShowGetOTP &&
        !publicFormMachineState.context.isLoggedIn
      ) {
        return true;
      }
      return false;
    };

    const renderSubmitButton = (): React.ReactElement | null => {
      if (isAuthenticationStep()) {
        return renderVerifyOtp();
      } else
        return (
          <ButtonComponent
            id={nextBtnId}
            isDisabled={isSubmitButtonLoading() || isSubmitButtonDisabled()}
            isLoading={isSubmitButtonLoading()}
            type={'button'}
            variant={buttonVariant.primary as ButtonVariant}
            size={buttonSize.large as ButtonSize}
            onClick={onClickNextBtn}
            className={customClassNames.nextAndSubmitButton}
            shouldShrinkButtonWhileLoading={false}
            designSystemType={designSystemType}
          >
            <SubmitButtonTextWrapper className={`${stepId}-text-wrapper`}>
              <SubmitButtonFrame
                className={`${stepId}-frame`}
              ></SubmitButtonFrame>
              <SubmitButtonText className={`${stepId}-text`}>
                {nextBtnText}
              </SubmitButtonText>
            </SubmitButtonTextWrapper>
          </ButtonComponent>
        );
    };

    const renderCTADetails = (): React.ReactElement | null => {
      const ctaDetails = stepInfo?.cta_details;
      if (ctaDetails && ctaDetails.length > 0) {
        return (
          <>
            {ctaDetails.map((eachCTADetails, index) => {
              if (eachCTADetails.cta_type === 'LIMITED_SEAT_AVAILABLE') {
                return (
                  <LimitedSeatMessageContainer key={index}>
                    <FireIcon />
                    <LimitedSeatMessageText className={'cta-limited-seats-text'}>
                      {t('publicRegistrationForm.limitedSeatsAvailable')}
                    </LimitedSeatMessageText>
                  </LimitedSeatMessageContainer>
                );
              } else return null;
            })}
          </>
        );
      } else return null;
    };

    const handleAdditionalCTAButtonClick = (
      cta_btn_actions: string[] | undefined,
      cta_type: 'SKIP' | undefined
    ) => {
      if (!cta_btn_actions) return;

      if (cta_type === 'SKIP') {
        //NOTE: This is a dummy email to bypass the validation, need to improve this logic
        formApi.setValue('email', 'dummy#@gmail.com');
      }

      publicFormMachineSend({
        type: productFormsMachineEvents.assignConfigEventsToContext,
        configEvent: cta_btn_actions,
        values: formValues
      } as AssignConfigEventToContext);
      publicFormMachineSend({
        type: productFormsMachineEvents.callEventsFromConfig
      } as CallEventsFromConfig);
    };

    const renderAdditionalCTAButton = (): React.ReactElement | null => {
      // Note : Adding for Skip purpose

      if (!stepInfo?.submit_btn_cta_details) {
        return null;
      }

      const ctaDetails = stepInfo.submit_btn_cta_details;
      const { cta_text, cta_btn_id, cta_btn_actions, cta_type } = ctaDetails;
      return (
        <AdditionalCTAButton
          id={cta_btn_id}
          onClick={() =>
            handleAdditionalCTAButtonClick(cta_btn_actions, cta_type)
          }
          className={'additionalCTAButtonClassName'}
        >
          {cta_text}
        </AdditionalCTAButton>
      );
    };

    const renderTnCText = (): React.ReactElement | null => {
      const tncText = stepInfo?.tnc_text;
      if (tncText) {
        return (
          <TnCText
            className={'termsAndConditionsTextClassName'}
            dangerouslySetInnerHTML={{
              __html: tncText
            }}
          />
        );
      } else return null;
    };

    const renderCTAAndSubmitButton = (): React.ReactElement | null => {
      return (
        <>
          {renderCTADetails()}
          {renderSubmitButton()}
        </>
      );
    };

    const renderLimitedTimeOfferUI = (
      description: string
    ): React.ReactElement => {
      return (
        <LimitedTimeOfferContainer
          className={customClassNames.limitedTimeOfferContainer}
        >
          <PriceContainer
            dangerouslySetInnerHTML={{
              __html: description
            }}
          />
        </LimitedTimeOfferContainer>
      );
    };

    const renderFooterUiDetails = (): React.ReactElement | null => {
      const extraFooterUiDetails = stepInfo?.extra_footer_ui_details;

      if (!extraFooterUiDetails) {
        return <>{renderCTAAndSubmitButton()}</>;
      }

      const { type, description } = extraFooterUiDetails;

      if (type === 'LIMITED_TIME_OFFER_UI') {
        return (
          <ExtraFooterUIContainer>
            {renderLimitedTimeOfferUI(description)}
            <CTAAndSubmitButtonContainer>
              {renderCTAAndSubmitButton()}
            </CTAAndSubmitButtonContainer>
          </ExtraFooterUIContainer>
        );
      }

      return <>{renderCTAAndSubmitButton()}</>;
    };

    const renderFormFooter = (): React.ReactElement | null => {
      //Note: nextStepDetails will be empty in case of Success Step with no fields
      if (nextStepDetails)
        return (
          <MultiStepFormFooterWrapper
            className={customClassNames.publicRegistrationFormFooterBtnWrapper}
          >
            {renderFooterUiDetails()}
            {renderAdditionalCTAButton()}
            {renderTnCText()}
            <ErrorMessage>
              {publicFormMachineState.context.errorMessage}
            </ErrorMessage>
          </MultiStepFormFooterWrapper>
        );
      return null;
    };

    const renderFormFields = (): React.ReactElement | null => {
      //NOTE: StepFields will be empty in case of Success Step with no fields
      return stepFields.length > 0 ? (
        <FormRenderer
          informedFieldsInfo={{
            formValues,
            errors,
            touched,
            formApi,
            stepFields
          }}
          registrationFormState={publicFormMachineState}
          registrationFormSend={publicFormMachineSend}
          validationSchemaObject={validationSchemaObject}
          customClassNames={customClassNames}
          handleFormReset={handleFormReset}
          designSystemType={designSystemType}
        />
      ) : null;
    };

    const onClickBackButton = () => {
      if (isAuthenticationStep()) {
        formApi.setValue('otp', '');
      }
      publicFormMachineSend({
        type: productFormsMachineEvents.assignConfigEventsToContext,
        configEvent: prevBtnActions,
        values: formValues
      } as AssignConfigEventToContext);
      publicFormMachineSend({
        type: productFormsMachineEvents.callEventsFromConfig
      } as CallEventsFromConfig);
    };

    const renderOtpFieldHeading = () => {
      return (
        <OTPDescriptionText className={'OTPDescriptionTextClassName'}>
          {t('login.weHaveSentOTPtoYourMobileNumber')}
          <PhoneNumberText className={'phoneNumberTextClassClassName'}>
            {` ${DialCodes.india}`}&nbsp;{formValues['phone']}
          </PhoneNumberText>
        </OTPDescriptionText>
      );
    };

    const renderGoBackButton = (): React.ReactElement => {
      if (prevStepId && !prevBtnText) {
        return (
          <GoBackContainer
            type={'button'}
            className={customClassNames.multiStepGoBackBtn}
            id={prevBtnId}
            onClick={onClickBackButton}
          >
            <BackArrowIcon />
          </GoBackContainer>
        );
      } else if (prevStepId && prevBtnText) {
        return (
          <GoBackContainer
            type={'button'}
            className={customClassNames.multiStepGoBackBtn}
            id={prevBtnId}
            onClick={onClickBackButton}
          >
            <ChevronLeftArrowIcon />
            <GoBackText className="go-back-button-text">{prevBtnText}</GoBackText>
          </GoBackContainer>
        );
      } else return <></>;
    };

    const renderGoogleCalendarInviteButton = (): React.ReactElement | null => {
      const { selectADateToBookASlot, timeSlots } = formValues;
      return (
        <GoogleCalendarInviteComponent
          selectADateToBookASlot={selectADateToBookASlot as string}
          timeSlots={timeSlots as string}
          stepInfo={stepInfo}
        />
      );
    };

    const renderEmbeddedFormSuccessMessage = (): React.ReactElement => {
      return (
        <SuccessMessageContainer
          className={customClassNames.embeddedFormSuccessMessageContainer}
        >
          <IconContainer>
            <MediaQuery maxWidth={768}>
              <SuccessTickIcon height={48} width={48} />
            </MediaQuery>
            <MediaQuery minWidth={769}>
              <SuccessTickIcon height={74} width={74} />
            </MediaQuery>
          </IconContainer>
          <SuccessHeading
            className="step-info-title"
            dangerouslySetInnerHTML={{
              __html: stepInfo?.title
            }}
          />
          <LineBreak
            className={customClassNames.embeddedFormSuccessMessageLineBreak}
          />
          <SuccessDescription
            dangerouslySetInnerHTML={{
              __html: stepInfo?.description
            }}
            className={customClassNames.embeddedFormSuccessMessageDescription}
          />
        </SuccessMessageContainer>
      );
    };

    const renderProgressBar = (): React.ReactElement | null => {
      if (stepInfo?.progress_bar_info) {
        const { type, total_steps, current_step } = stepInfo.progress_bar_info;
        if (type === progressBarType.stepProgressBar) {
          return (
            <ProgressBarWithSteps
              totalProgressBars={total_steps}
              completedBars={current_step}
            />
          );
        } else if (type === progressBarType.numberedStepProgressBar) {
          return (
            <NumberedProgressbar
              totalProgressBars={total_steps}
              currentStep={current_step}
              numberedProgressBarStepsTitles={getNumberedProgressBarTitles()}
              customStyles={
                stepInfo?.progress_bar_info?.step_details?.customStyles
              }
            />
          );
        } else {
          const progressBarPercentage = (current_step / total_steps) * 100;
          return (
            <ProgressBarContainer>
              <ProgressBar width={progressBarPercentage} />
            </ProgressBarContainer>
          );
        }
      }
      return null;
    };

    const renderFormTitle = (): React.ReactElement | null => {
      if (!stepInfo?.title) {
        return null;
      }
      return (
        <FormTitle
          className={'public-registration-form-heading'}
          dangerouslySetInnerHTML={{ __html: stepInfo.title }}
        />
      );
    };

    const renderFormDescription = (): React.ReactElement | null => {
      if (!stepInfo?.description) {
        return null;
      }
      return (
        <FormDescription
          className={'public-registration-form-description'}
          dangerouslySetInnerHTML={{ __html: stepInfo.description }}
        />
      );
    };

    const renderRedirectionUrls = (condition) => {
      if (condition) {
        //NOtE: values will be used in eval
        const values = formValues;
        condition.forEach((redirectionUrlDetail: object) => {
          const { redirection_condition, redirection_url } = redirectionUrlDetail;
          if (eval(redirection_condition)) {
            const targetUrl = new URL(redirection_url);
            const validatedUrl =
              verifyURLParamsBwLinkAndRedirectionLink(targetUrl);
            window.location.href = removeEmptyParams(validatedUrl);
          }
        });
      }
    };

    const renderCustomFormHeader = (): React.ReactElement | null => {
      return customHeader ? customHeader(eachFormStep) : null;
    };

    const renderFormHeader = (): React.ReactElement | null => {
      switch (currentStepType) {
        case stepTypes.EMBEDDED_SUCCESS_FORM:
          return (
            <>
              {renderProgressBar()}
              {renderEmbeddedFormSuccessMessage()}
            </>
          );
        case stepTypes.EMBEDDED_SUCCESS_PAGE:
        case stepTypes.EMBEDDED_SUCCESS_PAGE_WITHOUT_SLOTS: {
          const slotDate =
            (formValues['selectADateToBookASlot'] as string) ??
            (formValues['webinar1DateTime'] as string);
          const slotTime =
            (formValues['timeSlots'] as string) ??
            (formValues['preferredWebinarSlot'] as string);
          const isEmbeddedSuccessPageWithOutSlots =
            stepTypes.EMBEDDED_SUCCESS_PAGE_WITHOUT_SLOTS === currentStepType;

          return (
            <>
              <EmbeddedFormSuccessPage
                slotDate={
                  isEmbeddedSuccessPageWithOutSlots ? undefined : slotDate
                }
                slotTime={
                  isEmbeddedSuccessPageWithOutSlots ? undefined : slotTime
                }
                title={stepInfo?.title}
                description={stepInfo?.description}
                checkAndDoRedirection={() => {
                  conditionBasedRedirectionUrls &&
                    renderRedirectionUrls(conditionBasedRedirectionUrls);
                }}
              />
              {stepInfo?.calendar_invite_cta_details &&
                renderGoogleCalendarInviteButton()}
            </>
          );
        }
        case stepTypes.WEBINAR_CTA_SUCCESS_PAGE: {
          const slotDate =
            (formValues['selectADateToBookASlot'] as string) ??
            (formValues['webinar1DateTime'] as string);
          const slotTime =
            (formValues['timeSlots'] as string) ??
            (formValues['preferredWebinarSlot'] as string);
          const email = formValues['email'] as string | undefined;

          return (
            <SuccessMessageContainerWithEmailMessage
              title={stepInfo?.title}
              description={stepInfo?.description}
              slotDate={slotDate}
              slotTime={slotTime}
              email={email}
            />
          );
        }
        case stepTypes.CUSTOM_SUCCESS_PAGE: {
          return renderCustomSuccessPage ? (
            renderCustomSuccessPage(
              formValues,
              publicFormMachineState as PublicFormMachineStateType,
              publicFormMachineSend as PublicFormMachineSendType
            )
          ) : (
            <></>
          );
        }
        default: {
          const customImageUrl = stepInfo?.custom_image_url;
          return (
            <>
              {renderProgressBar()}
              {(customHeader || stepInfo?.extra_header_ui_details) &&
                renderCustomFormHeader()}
              {renderGoBackButton()}
              {renderFormTitle()}
              {renderFormDescription()}
              {customImageUrl && (
                <CustomUIImage
                  src={customImageUrl}
                  className="custom-image-url-class"
                />
              )}
              {currentStepType === stepTypes.AUTHENTICATION_STEP &&
                renderOtpFieldHeading()}
            </>
          );
        }
      }
    };

    return (
      <Multistep.Step step={stepId} next={eval(nextStepId)} previous={prevStepId}>
        <FormFieldSet>
          {renderFormHeader()}
          {renderFormFields()}
          {renderFormFooter()}
        </FormFieldSet>
      </Multistep.Step>
    );
  };
